import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import Providers from './components/Providers'
import Layout from './components/Layout/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import Home from './components/Pages/Home'
import DashboardLayout from './components/Dashboard/DashboardLayout'
import Overview from './components/Dashboard/Overview'
import Messaging from './components/Dashboard/Messaging'
import DeviceList from './components/Dashboard/DeviceList'
import ApiManagement from './components/Dashboard/ApiManagement'
import StoplightStyleDocs from './components/Dashboard/StoplightStyleDocs'
import AccountSettings from './components/Dashboard/AccountSettings'
import Webhooks from './components/Dashboard/Webhooks'
import Pricing from './components/Pages/Pricing'
import Support from './components/Pages/Support'
import Checkout from './components/Pages/Checkout'
import ForgotPassword from './components/Pages/ForgotPassword'
import ResetPassword from './components/Pages/ResetPassword'
import VerifyEmail from './components/Pages/VerifyEmail'
import Download from './components/Pages/Download'
import Login from './components/Pages/Login'
import { setNavigationCallback } from './lib/api'
// Docs Components
import DocsLayout from './components/Docs/DocsLayout'
import Introduction from './components/Docs/pages/Introduction'
import QuickStart from './components/Docs/pages/QuickStart'
import SendSMS from './components/Docs/pages/SendSMS'
// Admin Components
import { AdminAuthProvider } from './contexts/AdminAuthContext'
import AdminLogin from './components/Admin/AdminLogin'
import AdminLayout from './components/Admin/AdminLayout'
import AdminDashboard from './components/Admin/AdminDashboard'
import UserManagement from './components/Admin/UserManagement'
import AdminSystem from './components/Admin/AdminSystem'
import PlanManagement from './components/Admin/PlanManagement'
import AdminAnalytics from './components/Admin/AdminAnalytics'
import AdminBranding from './components/Admin/AdminBranding'
import AdminProtectedRoute from './components/Admin/AdminProtectedRoute'

function NavigationSetup() {
  const navigate = useNavigate()
  
  useEffect(() => {
    // Set up the navigation callback for API auth redirects
    setNavigationCallback((path: string) => {
      navigate(path, { replace: true })
    })
  }, [navigate])
  
  return null
}

function App() {
  return (
    <Providers>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <NavigationSetup />
        <Routes>
          {/* Regular App Routes */}
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="dashboard" element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
              {/* Nested dashboard routes */}
              <Route index element={<Overview />} />
              <Route path="messages" element={<Messaging />} />
              <Route path="devices" element={<DeviceList />} />
              <Route path="devices/:deviceId" element={<DeviceList />} />
              <Route path="api" element={<ApiManagement />} />
              <Route path="webhooks" element={<Webhooks />} />
              <Route path="settings" element={<AccountSettings />} />
            </Route>
            <Route path="pricing" element={<Pricing />} />
            <Route path="support" element={<Support />} />
            <Route path="checkout/:planName" element={<Checkout />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="verify-email" element={<VerifyEmail />} />
            <Route path="download" element={<Download />} />
            <Route path="login" element={<Login />} />
          </Route>

          {/* Documentation Routes */}
          <Route path="/docs" element={<DocsLayout />}>
            <Route index element={<Introduction />} />
            <Route path="quickstart" element={<QuickStart />} />
            <Route path="api/send-sms" element={<SendSMS />} />
          </Route>

          {/* Standalone API Documentation */}
          <Route
            path="/api-docs"
            element={
              <ProtectedRoute>
                <StoplightStyleDocs />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes - Separate from main layout */}
          <Route path="/admin" element={
            <AdminAuthProvider>
              <AdminLogin />
            </AdminAuthProvider>
          } />
          <Route path="/admin/*" element={
            <AdminAuthProvider>
              <AdminProtectedRoute>
                <AdminLayout />
              </AdminProtectedRoute>
            </AdminAuthProvider>
          }>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="plans" element={<PlanManagement />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="support" element={<div className="p-8">Support Management - Coming Soon</div>} />
            <Route path="system" element={<AdminSystem />} />
            <Route path="branding" element={<AdminBranding />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </Providers>
  )
}

export default App